import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { Globe, Home, Moon, Sun, Palette } from 'lucide-react';
import { useTheme } from '../ThemeContext';
interface GeneralSectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const GeneralSection: React.FC<GeneralSectionProps> = ({
  setHasUnsavedChanges
}) => {
  const {
    theme,
    setTheme
  } = useTheme();
  const [general, setGeneral] = useState({
    language: 'en',
    timezone: 'auto',
    defaultLandingTab: 'social_feed',
    themePreference: theme
  });
  const languages = [{
    code: 'en',
    name: 'English'
  }, {
    code: 'es',
    name: 'Español'
  }, {
    code: 'fr',
    name: 'Français'
  }, {
    code: 'de',
    name: 'Deutsch'
  }, {
    code: 'it',
    name: 'Italiano'
  }, {
    code: 'pt',
    name: 'Português'
  }, {
    code: 'ru',
    name: 'Русский'
  }, {
    code: 'ja',
    name: '日本語'
  }, {
    code: 'zh',
    name: '中文'
  }];
  const timezones = [{
    value: 'auto',
    label: 'Auto-detect (Browser)'
  }, {
    value: 'America/New_York',
    label: 'Eastern Time (ET)'
  }, {
    value: 'America/Chicago',
    label: 'Central Time (CT)'
  }, {
    value: 'America/Denver',
    label: 'Mountain Time (MT)'
  }, {
    value: 'America/Los_Angeles',
    label: 'Pacific Time (PT)'
  }, {
    value: 'Europe/London',
    label: 'Greenwich Mean Time (GMT)'
  }, {
    value: 'Europe/Paris',
    label: 'Central European Time (CET)'
  }, {
    value: 'Asia/Tokyo',
    label: 'Japan Standard Time (JST)'
  }, {
    value: 'Australia/Sydney',
    label: 'Australian Eastern Time (AET)'
  }];
  const landingTabs = [{
    value: 'social_feed',
    label: 'Social Feed'
  }, {
    value: 'discovery_feed',
    label: 'Discovery Feed'
  }, {
    value: 'chat',
    label: 'Chat'
  }, {
    value: 'stats',
    label: 'Stats'
  }, {
    value: 'creator_dashboard',
    label: 'Creator Studio'
  }];
  const themes = [{
    value: 'light',
    label: 'Light',
    icon: Sun
  }, {
    value: 'dark',
    label: 'Dark',
    icon: Moon
  }, {
    value: 'neon',
    label: 'Neon',
    icon: Palette
  }];
  const handleChange = (field: string, value: string) => {
    setGeneral(prev => ({
      ...prev,
      [field]: value
    }));
    // If changing theme, update the theme context
    if (field === 'themePreference') {
      setTheme(value);
    }
    setHasUnsavedChanges(true);
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18634">
      <SettingsCard title="Language & Region" description="Set your preferred language and timezone." data-id="element-18635">
        <div className="space-y-6" data-id="element-18636">
          {/* Language */}
          <div data-id="element-18637">
            <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18638">
              Language
            </label>
            <div className="relative" data-id="element-18639">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-18640">
                <Globe size={16} className="text-gray-500" data-id="element-18641" />
              </div>
              <select id="language" value={general.language} onChange={e => handleChange('language', e.target.value)} className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18642">
                {languages.map(lang => <option key={lang.code} value={lang.code} data-id="element-18643">
                    {lang.name}
                  </option>)}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18644">
              Choose the language for the SinShift interface.
            </p>
          </div>
          {/* Timezone */}
          <div data-id="element-18645">
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18646">
              Timezone
            </label>
            <select id="timezone" value={general.timezone} onChange={e => handleChange('timezone', e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18647">
              {timezones.map(tz => <option key={tz.value} value={tz.value} data-id="element-18648">
                  {tz.label}
                </option>)}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18649">
              Set your timezone for accurate time displays.
            </p>
          </div>
        </div>
      </SettingsCard>
      <SettingsCard title="Application Preferences" description="Customize your SinShift experience." data-id="element-18650">
        <div className="space-y-6" data-id="element-18651">
          {/* Default Landing Tab */}
          <div data-id="element-18652">
            <label htmlFor="default-tab" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18653">
              Default Landing Tab
            </label>
            <div className="relative" data-id="element-18654">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-18655">
                <Home size={16} className="text-gray-500" data-id="element-18656" />
              </div>
              <select id="default-tab" value={general.defaultLandingTab} onChange={e => handleChange('defaultLandingTab', e.target.value)} className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18657">
                {landingTabs.map(tab => <option key={tab.value} value={tab.value} data-id="element-18658">
                    {tab.label}
                  </option>)}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18659">
              Choose which tab to show when you first open SinShift.
            </p>
          </div>
          {/* Theme */}
          <div data-id="element-18660">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-18661">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-18662">
              {themes.map(themeOption => {
              const Icon = themeOption.icon;
              return <button key={themeOption.value} className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${general.themePreference === themeOption.value ? 'border-pink-500 dark:border-purple-500 bg-pink-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => handleChange('themePreference', themeOption.value)} data-id="element-18663">
                    <Icon size={24} className={general.themePreference === themeOption.value ? 'text-pink-500 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'} data-id="element-18664" />
                    <span className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18665">
                      {themeOption.label}
                    </span>
                  </button>;
            })}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-18666">
              Choose your preferred theme for the SinShift interface.
            </p>
          </div>
        </div>
      </SettingsCard>
    </div>;
};